import { Box, Grid2 as Grid, Typography, useTheme } from '@mui/material'
import SingleOrganizationSelector from 'components/SingleOrganizationSelector'
import useAuth from 'hooks/useAuth'
import { useFetchLedgerAccounts } from 'hooks/useLedgerAccounts'
import {
  useCreateOrganization,
  useDeleteOrganization,
  useFetchExpandedOrganization,
  useFetchOrganizationSettings,
  useUpdateOrganization,
} from 'hooks/useOrganizations'
import { useRoles } from 'hooks/useRoles'
import { useCreateUser, useUpdateUser } from 'hooks/useUsers'
import type { FC } from 'react'
import { startTransition, Suspense, useCallback, useEffect, useRef, useState } from 'react'
import { findOrganizationById, getOrganizationChildrenCount } from 'types/organization'
import EditOrganization from './EditOrganization'
import OrganizationTree from './OrganizationTree'
import { useOrganizationActions } from './hooks/useOrganizationActions'

const ManageOrganizationStructure: FC = () => {
  const theme = useTheme()
  const { token, profile, refetchProfile } = useAuth()
  const [needsRefresh, setNeedsRefresh] = useState(false)
  const { roles, isLoading: isRolesLoading, isError: isRolesError } = useRoles(token!, true)
  const [rootOrganizationId, setRootOrganizationId] = useState('')
  const [rootTeamId, setRootTeamId] = useState('')
  const [selectedOrganizationId, setSelectedOrganizationId] = useState('')
  const { organizationHierarchy, isLoading, isError, refetch } = useFetchExpandedOrganization(rootTeamId, token!, false)

  const {
    createOrganization,
    isLoading: isCreatingOrg,
    error: createOrgError,
    createdOrganization,
  } = useCreateOrganization(token!)

  const { deleteOrganization, isLoading: isDeletingOrg, error: deleteOrgError } = useDeleteOrganization(token!)

  const { createUser, isLoading: isCreatingUser, error: createUserError } = useCreateUser(token!)
  const { updateUser, isLoading: isUpdatingUser, error: updateUserError } = useUpdateUser(token!)

  const {
    ledgerAccounts,
    isLoading: isLoadingLedgerAccounts,
    isError: isLedgerAccountsError,
    refetch: refetchLedgerAccounts,
  } = useFetchLedgerAccounts(rootOrganizationId, token!, rootOrganizationId !== '')

  const {
    settings: organizationSettings,
    isLoading: isLoadingSettings,
    isError: isSettingsError,
    refetch: refetchSettings,
  } = useFetchOrganizationSettings(rootTeamId, token!)

  const ledgerAccountsFetchedRef = useRef<string | null>(null)

  const { updateOrganization, isLoading: isUpdatingOrg, error: updateOrgError } = useUpdateOrganization(token!)

  const handleRefreshNeeded = useCallback(() => {
    setNeedsRefresh(true)
    setSelectedOrganizationId('')
  }, [])

  const {
    isSubmitting,
    setIsSubmitting,
    handleOrganizationCreated,
    handleOrganizationDeleted,
    handleOrganizationUpdated,
    handleUserAction,
  } = useOrganizationActions({
    createOrganization,
    deleteOrganization,
    updateOrganization,
    createUser,
    updateUser,
    isCreatingOrg,
    isDeletingOrg,
    isUpdatingOrg,
    isCreatingUser,
    isUpdatingUser,
    createdOrganization,
    onRefreshNeeded: handleRefreshNeeded,
  })

  const handleOrganizationChange = (selected: string) => {
    startTransition(() => {
      setRootTeamId(selected)
      setSelectedOrganizationId(selected)
      if (organizationHierarchy) {
        const newRootOrgId = organizationHierarchy.organization.rootOrganizationId || ''
        setRootOrganizationId(newRootOrgId)
        ledgerAccountsFetchedRef.current = null
      }
      refetchSettings()
    })
  }

  useEffect(() => {
    if (organizationHierarchy) {
      const newRootOrgId = organizationHierarchy.organization.rootOrganizationId || ''
      setRootOrganizationId(newRootOrgId)

      if (ledgerAccountsFetchedRef.current !== newRootOrgId) {
        refetchLedgerAccounts()
        ledgerAccountsFetchedRef.current = newRootOrgId
      }
    }
  }, [organizationHierarchy, rootTeamId, refetchLedgerAccounts])

  // Refetch data when component mounts
  useEffect(() => {
    refetch()
    refetchProfile()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (needsRefresh) {
      Promise.all([refetch(), refetchProfile()]).finally(() => {
        setNeedsRefresh(false)
        setIsSubmitting(false)
      })
    }
  }, [needsRefresh, refetch, refetchProfile, setIsSubmitting])

  useEffect(() => {
    if (createOrgError) {
      console.error('Error creating organization:', createOrgError)
      setIsSubmitting(false)
    }
  }, [createOrgError, setIsSubmitting])

  const isAnyLoading =
    isLoading ||
    isRolesLoading ||
    isCreatingOrg ||
    isDeletingOrg ||
    isLoadingLedgerAccounts ||
    isLoadingSettings ||
    isUpdatingOrg

  const hasError =
    isError ||
    isRolesError ||
    createOrgError ||
    deleteOrgError ||
    isLedgerAccountsError ||
    isSettingsError ||
    updateOrgError

  const renderContent = () => {
    if (isAnyLoading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexGrow: 1 }}>
          <Typography>Loading...</Typography>
        </Box>
      )
    }

    if (hasError) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexGrow: 1 }}>
          <Typography color="error">Error: {createOrgError?.message || 'Error loading data'}</Typography>
        </Box>
      )
    }

    if (!rootTeamId) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
          <Typography variant="h5" color="text.secondary">
            Select a Team to Edit
          </Typography>
        </Box>
      )
    }

    const selectedOrganization =
      selectedOrganizationId && organizationHierarchy
        ? findOrganizationById(organizationHierarchy, selectedOrganizationId)
        : organizationHierarchy

    const childrenCount = selectedOrganization
      ? getOrganizationChildrenCount(organizationHierarchy, selectedOrganization.organization.id)
      : 0

    const hasChildren = childrenCount > 0

    return (
      <Grid container spacing={2} sx={{ height: '100%' }}>
        <Grid size={{ xs: 12, md: 8 }} sx={{ height: '100%', overflow: 'auto' }}>
          {organizationHierarchy && (
            <OrganizationTree
              organizationHierarchy={organizationHierarchy}
              setSelectedOrganization={setSelectedOrganizationId}
              theme={theme}
              token={token!}
              onOrganizationCreated={handleOrganizationCreated}
              setNeedsRefresh={setNeedsRefresh}
              isSubmitting={isSubmitting || isCreatingOrg}
            />
          )}
        </Grid>
        <Grid size={{ xs: 12, md: 4 }} sx={{ height: '100%' }}>
          {selectedOrganization ? (
            <Box sx={{ height: '100%' }}>
              <EditOrganization
                organization={selectedOrganization.organization}
                users={selectedOrganization.users}
                theme={theme}
                roles={roles}
                onDeleteOrganization={handleOrganizationDeleted}
                onUserAction={handleUserAction}
                hasChildren={hasChildren}
                isSubmitting={isSubmitting || isCreatingUser || isUpdatingUser}
                error={createUserError || updateUserError}
                ledgerAccounts={ledgerAccounts || []}
                organizationSettings={organizationSettings || []}
                onUpdateOrganization={handleOrganizationUpdated}
                userProfile={profile!}
                setRefreshNeeded={setNeedsRefresh}
                token={token!}
              />
            </Box>
          ) : null}
        </Grid>
      </Grid>
    )
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 160px)' }}>
      <Box sx={{ p: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid size={{ xs: 8 }}>
            <Typography variant="h2">Manage Team Structure</Typography>
          </Grid>
          <Grid size={{ xs: 4 }}>
            <SingleOrganizationSelector
              userProfile={profile!}
              selectedOrganizationId={rootTeamId}
              handleChange={handleOrganizationChange}
              width={400}
              labelBackgroundColor={theme.palette.background.default}
            />
          </Grid>
        </Grid>
      </Box>

      <Box sx={{ flexGrow: 1, overflow: 'hidden', p: 2 }}>
        <Suspense fallback={<Typography>Loading...</Typography>}>{renderContent()}</Suspense>
      </Box>
    </Box>
  )
}

export default ManageOrganizationStructure
